import { EnumInvitationStatus } from '@calm/evexia-analytics';
import {
    CircleInvitation,
    CircleValidationFieldWithValue,
    CustomAxiosError,
} from '@calm/evexia-web-sdk';

/**
 * Describes the status of an invitation
 *
 * Do not use the following statuses:
 * - Deleted
 */
export enum InvitationStatus {
    Pending = 'pending',
    Claimed = 'claimed',
    Expired = 'expired',
    Deleted = 'deleted',
}

export const InviteCodeFieldName = 'invite_code';

export type InvitationErrorType = 'expired' | 'claimed' | 'country';

export const InvitationErrorMessage: Record<InvitationErrorType, string> = {
    expired: 'invitation_expired',
    claimed: 'invitation_claimed',
    country: 'invitation_country',
};

export const InvitationPartnerErrorMessage = {
    country: 'no_dependent_group_for_country_code',
};

export function getInviteValidationFields(
    inviteCode: string
): CircleValidationFieldWithValue[] {
    return [
        {
            name: InviteCodeFieldName,
            value: inviteCode,
        },
    ];
}

export function createInvitationError(
    type: InvitationErrorType
): CustomAxiosError {
    return {
        response: {
            status: 400,
            data: {
                message: InvitationErrorMessage[type],
            },
        },
    } as CustomAxiosError;
}

export function isInvitationError(e: CustomAxiosError): boolean {
    const errorType = identifyInvitationErrorType(e);
    return Boolean(errorType);
}

export function identifyInvitationErrorType(
    e: CustomAxiosError | null
): InvitationErrorType | undefined {
    if (!e) {
        return;
    }

    if (e?.response?.status && e?.response?.status >= 400) {
        if (!!e?.response?.data && typeof e.response.data === 'object') {
            // Handle either errorCode or message having the error
            if ('errorCode' in e.response.data) {
                switch (e?.response?.data?.errorCode) {
                    case InvitationErrorMessage.claimed:
                        return 'claimed';
                    case InvitationErrorMessage.country:
                        return 'country';
                    case InvitationPartnerErrorMessage.country:
                        return 'country';
                    case InvitationErrorMessage.expired:
                        return 'expired';
                }
            }
            if ('message' in e.response.data) {
                switch (e?.response?.data?.message) {
                    case InvitationErrorMessage.claimed:
                        return 'claimed';
                    case InvitationErrorMessage.country:
                        return 'country';
                    case InvitationPartnerErrorMessage.country:
                        return 'country';
                    case InvitationErrorMessage.expired:
                        return 'expired';
                }
            }
        }
    }
}

/*
 * Pending: invitation.claimedAt == null && invitation not expired
 * Claimed: invitation.claimedAt != null
 * Expired: invitation.expiresAt before now in UTC
 * Deleted: invitation.removedAt != null
 */
export function getInvitationStatus(
    invitation: CircleInvitation
): InvitationStatus {
    if (invitation?.claimedAt) {
        return InvitationStatus.Claimed;
    }
    if (invitation?.removedAt) {
        return InvitationStatus.Deleted;
    }
    if (new Date(invitation.expiresAt).getTime() <= Date.now()) {
        return InvitationStatus.Expired;
    }
    return InvitationStatus.Pending;
}

export const mapInvitationStatusAnalytics = (
    status?: string
): EnumInvitationStatus => {
    if (status === 'pending') return EnumInvitationStatus.Pending;
    if (status === 'claimed') return EnumInvitationStatus.Claimed;
    if (status === 'expired') return EnumInvitationStatus.Expired;
    return EnumInvitationStatus.Unclaimed;
};
